import { useMemo } from 'react';
import type { HealthCheckStatus } from '@gonfalon/health-check/types';
import { getMetricHealthResultQuery } from '@gonfalon/rest-api/queries/metricHealthCheck';
import { useProjectKey } from '@gonfalon/router/useProjectKey';
import { useQuery } from '@tanstack/react-query';

import { DataSourceType } from '../../constants';
import { MetricKind } from '../../MetricKind';

import type { MetricDefinition, MetricHealthResponse } from './constants';
import { getHealthList } from './getHealthList';

const getEventKey = (metric: MetricDefinition) => {
  switch (metric.kind) {
    case MetricKind.CUSTOM:
      return metric.eventKey;
    case MetricKind.PAGEVIEW:
    case MetricKind.CLICK:
      // TODO: remove this once the flag is merged
      // [report-event-keys-for-click-and-page-view-events] flag that determines whether to use eventKey or versionId.
      // Currently the PR is open: https://github.com/launchdarkly/gonfalon/pull/51503
      // oxlint-disable-next-line no-constant-condition
      if (false) {
        return metric.eventKey;
      } else {
        return metric._versionId;
      }
    default:
      return '';
  }
};

export const useHealthIndicator = (metric: MetricDefinition) => {
  const projectKey: string = useProjectKey();

  const eventKey = useMemo(() => getEventKey(metric), [metric]) || '';

  const hasRequiredData = useMemo(
    () => Boolean(eventKey) && metric.analysisUnits && metric.analysisUnits?.length > 0,
    [eventKey, metric.analysisUnits],
  );

  const query = useQuery({
    ...getMetricHealthResultQuery({
      projKey: projectKey,
      dataSourceKey: metric.dataSource?.key || DataSourceType.LAUNCHDARKLY_HOSTED,
      eventKind: metric.kind || 'custom',
      eventKey,
    }),
    enabled: hasRequiredData,
  });

  const isError = useMemo(() => query.isError && !query?.data, [query.isError, query.data]);

  const statusEntries = useMemo(() => {
    if (query.isLoading) {
      return [];
    }
    return getHealthList(eventKey, metric, isError, query.data as MetricHealthResponse);
  }, [metric, eventKey, isError, query.isLoading, query.data]);

  const overallStatus: HealthCheckStatus = useMemo(() => {
    // If missing required data, we're waiting
    if (!hasRequiredData) {
      return 'waiting';
    }

    if (isError) {
      return 'error';
    }

    if (query.isLoading) {
      return 'loading';
    }

    // Check if all status entries are WAITING
    const nonHeaderEntries = statusEntries.filter((e) => e?.status !== undefined);
    if (nonHeaderEntries.every((e) => e?.status === 'waiting')) {
      return 'waiting';
    }

    if (statusEntries.some((e) => e?.status === 'warning' || e?.status === 'fail')) {
      return 'warning';
    }

    return 'pass';
  }, [hasRequiredData, isError, query.isLoading, statusEntries]);

  return {
    ...query,
    statusEntries,
    overallStatus,
  };
};
